import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { StateView } from './record'
import { saveAttributes, type ProductAttribute } from './productApi'

/**
 * Характеристики товара: «название — значение — единица» в своём порядке (V60).
 *
 * Не пользовательские поля (#50): те объявляются один раз на все записи сущности, а у
 * дрели есть патрон, у краски — цвет, и общий список полей был бы пуст на три четверти.
 *
 * Правка — таблицей целиком и одним сохранением: добавить, изменить, удалить, переставить.
 * Сервер принимает список в новом порядке; строки с id правятся, без id добавляются,
 * пропавшие удаляются. Сохранять построчно значило бы оставить полупринятый порядок при
 * отказе посередине.
 */
interface Row {
  key: string
  id: string | null
  name: string
  value: string
  unit: string
}

const rowsOf = (attributes: ProductAttribute[]): Row[] =>
  attributes.map((attribute) => ({
    key: attribute.id, id: attribute.id, name: attribute.name, value: attribute.value, unit: attribute.unit ?? '',
  }))

let counter = 0

export function ProductAttributes({ productId, attributes, mayEdit, onSaved }: {
  productId: string
  attributes: ProductAttribute[]
  mayEdit: boolean
  onSaved: (attributes: ProductAttribute[]) => void
}) {
  const { t } = useApp()
  const [rows, setRows] = useState<Row[] | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const editing = rows != null

  const save = useMutation({
    mutationFn: (draft: Row[]) => saveAttributes(productId, draft.map((row) => ({
      id: row.id, name: row.name.trim(), value: row.value.trim(), unit: row.unit.trim() === '' ? null : row.unit.trim(),
    }))),
    onSuccess: (saved) => {
      setRows(null)
      setNotice(null)
      onSaved(saved)
    },
    onError: (error) => setNotice(error instanceof ApiError ? error.message : t('app.error')),
  })

  if (!editing) {
    return (
      <div className="stack" style={{ gap: 10 }}>
        {attributes.length === 0 ? (
          <StateView compact kind="empty" title={t('product.attr.empty')}
                     description={mayEdit ? t('product.attr.emptyText') : undefined} />
        ) : (
          <dl className="pa-list" aria-label={t('product.attr.title')}>
            {attributes.map((attribute) => (
              <div key={attribute.id} className="pa-row">
                <dt>{attribute.name}</dt>
                <dd>
                  {attribute.value}
                  {attribute.unit ? <span className="muted"> {attribute.unit}</span> : null}
                </dd>
              </div>
            ))}
          </dl>
        )}
        {mayEdit && (
          <div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setRows(rowsOf(attributes))}>
              <Icon name="edit" />
              {t('product.attr.edit')}
            </button>
          </div>
        )}
      </div>
    )
  }

  const update = (key: string, patch: Partial<Row>) =>
    setRows((current) => (current ?? []).map((row) => (row.key === key ? { ...row, ...patch } : row)))
  const move = (index: number, delta: number) =>
    setRows((current) => {
      const next = [...(current ?? [])]
      const target = index + delta
      if (target < 0 || target >= next.length) return next
      const [row] = next.splice(index, 1)
      if (row) next.splice(target, 0, row)
      return next
    })

  const names = rows.map((row) => row.name.trim().toLowerCase()).filter((name) => name !== '')
  const duplicate = names.find((name, index) => names.indexOf(name) !== index)
  const incomplete = rows.some((row) => row.name.trim() === '' || row.value.trim() === '')
  const blocked = duplicate ? t('product.attr.duplicate', { name: duplicate }) : incomplete ? t('product.attr.incomplete') : null

  return (
    <form
      className="stack"
      style={{ gap: 10 }}
      onSubmit={(event) => {
        event.preventDefault()
        if (!blocked) save.mutate(rows)
      }}
    >
      {notice && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span className="fg">{notice}</span>
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table className="tbl" aria-label={t('product.attr.title')}>
          <thead>
            <tr>
              <th>{t('product.attr.name')}</th>
              <th>{t('product.attr.value')}</th>
              <th style={{ width: 110 }}>{t('product.attr.unit')}</th>
              <th style={{ width: 120 }} aria-label={t('product.attr.actions')} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.key}>
                <td>
                  <input className="input input-sm" value={row.name} maxLength={128}
                         aria-label={t('product.attr.nameOf', { index: index + 1 })}
                         onChange={(event) => update(row.key, { name: event.target.value })} />
                </td>
                <td>
                  <input className="input input-sm" value={row.value} maxLength={512}
                         aria-label={t('product.attr.valueOf', { index: index + 1 })}
                         onChange={(event) => update(row.key, { value: event.target.value })} />
                </td>
                <td>
                  <input className="input input-sm" value={row.unit} maxLength={32}
                         aria-label={t('product.attr.unitOf', { index: index + 1 })}
                         onChange={(event) => update(row.key, { unit: event.target.value })} />
                </td>
                <td>
                  <span className="row" style={{ gap: 2 }}>
                    <button type="button" className="btn btn-ghost btn-sm btn-icon" disabled={index === 0}
                            aria-label={t('product.attr.up', { index: index + 1 })} onClick={() => move(index, -1)}>
                      <Icon name="arrow-up" size="sm" />
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm btn-icon" disabled={index === rows.length - 1}
                            aria-label={t('product.attr.down', { index: index + 1 })} onClick={() => move(index, 1)}>
                      <Icon name="arrow-down" size="sm" />
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm btn-icon"
                            aria-label={t('product.attr.remove', { index: index + 1 })}
                            onClick={() => setRows((current) => (current ?? []).filter((candidate) => candidate.key !== row.key))}>
                      <Icon name="trash" size="sm" />
                    </button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-ghost btn-sm"
                onClick={() => setRows((current) => [...(current ?? []), { key: `new-${++counter}`, id: null, name: '', value: '', unit: '' }])}>
          <Icon name="plus" />
          {t('product.attr.add')}
        </button>
        <span className="spacer" />
        {blocked && (
          <span className="flag flag-warning"><Icon name="alert" />{blocked}</span>
        )}
        <button type="button" className="btn btn-secondary btn-sm" disabled={save.isPending}
                onClick={() => { setRows(null); setNotice(null) }}>
          {t('card.cancel')}
        </button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={save.isPending || blocked != null}>
          {save.isPending ? t('adm.saving') : t('product.attr.save')}
        </button>
      </div>
    </form>
  )
}
